import { useForumType } from '@/components/hooks/useForumType';
import React from "react";
import classNames from "classnames";
import { Link } from "../../../lib/reactRouterWrapper";
import { commentPermalinkStyleSetting } from '@/lib/instanceSettings';
import { userIsPostCoauthor } from "../../../lib/collections/posts/helpers";
import { useCommentLinkState } from "./useCommentLink";
import { userIsAdmin } from "../../../lib/vulcan-users/permissions";
import { useFilteredCurrentUser } from "../../common/withUser";
import { AnalyticsContext } from "../../../lib/analyticsEvents";
import type { CommentTreeOptions } from "../commentTree";
import CommentShortformIcon from "./CommentShortformIcon";
import CommentDiscussionIcon from "./CommentDiscussionIcon";
import ShowParentComment from "../ShowParentComment";
import CommentUserName from "./CommentUserName";
import CommentsItemDate from "./CommentsItemDate";
import SmallSideVote from "../../votes/SmallSideVote";
import CommentOutdatedWarning from "./CommentOutdatedWarning";
import CommentsMenu from "../../dropdowns/comments/CommentsMenu";
import UserCommentMarkers from "../../users/UserCommentMarkers";
import { metaNoticeStyles } from "./metaNoticeStyles";
import { defineStyles, useStyles } from "@/components/hooks/useStyles";
import { getReviewLink } from "@/lib/reviewUtils";

const styles = defineStyles("CommentsItemMeta", (theme: ThemeType) => ({
  root: {
    "& > div": {
      marginRight: 5,
    },

    position: "relative",
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    rowGap: "6px",
    marginBottom: 8,
    color: theme.palette.text.dim,
    paddingTop: "0.6em",
    marginRight: 20,

    "& a:hover, & a:active": {
      textDecoration: "none",
      color: `${theme.palette.linkHover.dim} !important`,
    },
  },
  sideCommentMeta: {
    display: "flex",
    alignItems: "baseline",
  },
  metaNotice: {
    ...metaNoticeStyles(theme),
  },
  collapse: {
    marginRight: 5,
    opacity: 0.8,
    fontSize: "0.8rem",
    lineHeight: "1rem",
    paddingBottom: 2,
    display: "flex",
    verticalAlign: "middle",

    "& span": {
      fontFamily: "monospace",
    },
    "@media print": {
      display: "none",
    },
  },
  collapseChevron: {
    width: 15,
    transition: "transform 0.2s",
  },
  collapseChevronOpen: {
    transform: "rotate(90deg)",
  },
  collapseCharacter: {
    transform: 'translateY(0.75px)',
  },
  username: {
    marginRight: 6,

    "$sideCommentMeta &": {
      flexGrow: 1,
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      flexShrink: 1,
      display: "inline-block",
      overflowX: "hidden",
    },
  },
  userMarkers: {
    marginRight: 6,
  },
  moderatorHat: {
    marginRight: 8,
  },
  rightSection: {
    position: "absolute",
    right: -26,
    top: 12,
    display: "flex",
  },
  // When there are extra right-section elements (eg the participants list on
  // collapsed quick takes), keep the section in the flex flow so that it wraps
  // onto its own line rather than overlapping the vote buttons when the row is
  // too narrow.
  rightSectionInFlow: {
    position: "static",
    marginLeft: "auto",
    marginRight: -26,
    alignItems: "center",
  },
  linkIcon: {
    "--icon-size": "15.6px",
    verticalAlign: "top",
    color: theme.palette.icon.dim,
    margin: "0 4px",
    position: "relative",
    top: 1,
  },
  linkIconHighlighted: {
    strokeWidth: "0.7px",
    stroke: "currentColor",
    color: theme.palette.primary.main
  },
  menu: {
    opacity: 0.35,
    "@media print": {
      display: "none",
    },
  }
}));

export const CommentsItemMeta = ({
  treeOptions,
  comment,
  showCommentTitle,
  isParentComment,
  parentCommentId,
  showParentState,
  toggleShowParent,
  scrollIntoView,
  parentAnswerId,
  setSingleLine,
  collapsed,
  toggleCollapse,
  setShowEdit,
  rightSectionElements,
}: {
  treeOptions: CommentTreeOptions,
  comment: CommentsList|CommentsListWithParentMetadata,
  showCommentTitle: boolean,
  isParentComment?: boolean,
  parentCommentId?: string,
  showParentState: boolean,
  toggleShowParent: () => void,
  scrollIntoView?: () => void,
  parentAnswerId?: string,
  setSingleLine?: (singleLine: boolean) => void,
  collapsed?: boolean,
  toggleCollapse?: () => void,
  setShowEdit: () => void,
  rightSectionElements?: React.ReactNode,
}) => {
  const { forumType } = useForumType();
  const classes = useStyles(styles);
  const currentUserIsAdmin = useFilteredCurrentUser(u => userIsAdmin(u));
  const { scrollToCommentId } = useCommentLinkState();

  const {
    postPage, showCollapseButtons, post, tag, singleLineCollapse, isSideComment,
    hideActionsMenu, hideParentCommentToggle, hideParentCommentToggleForTopLevel,
  } = treeOptions;

  const authorIsPostAuthor = post &&
    (post.userId === comment.userId || userIsPostCoauthor(comment.user, post));
  const commentIsTopLevelShortform = post?.shortform && !comment.parentCommentId;

  const commentLinkProps = {
    comment,
    post,
    tag,
    scrollIntoView,
    scrollOnClick: postPage && !isParentComment,
  };

  /**
   * Show the moderator comment annotation if:
   * 1) it has the moderatorHat
   * 2) the user is either an admin, or the moderatorHat isn't deliberately hidden
   */
  const showModeratorCommentAnnotation = comment.moderatorHat && (
    currentUserIsAdmin
      ? true
      : !comment.hideModeratorHat
    );

  const moderatorCommentAnnotation = comment.hideModeratorHat
    ? "Moderator Comment (Invisible)"
    : "Moderator Comment";

  const reviewingForReview = comment.reviewingForReview;

  // Note: This could be decoupled from `commentPermalinkStyleSetting` without any side effects
  const highlightLinkIcon = commentPermalinkStyleSetting.get(forumType) === 'in-context' && scrollToCommentId === comment._id
  const menuVisible = (!isParentComment && !hideActionsMenu);

  return (
    <div className={classNames(
      classes.root,
      isSideComment && classes.sideCommentMeta,
    )}>
      {!parentCommentId && !comment.parentCommentId && isParentComment &&
        <div>○</div>
      }
      {post && <CommentShortformIcon comment={comment} post={post} />}
      {!showCommentTitle && <CommentDiscussionIcon comment={comment} small />}
      {!hideParentCommentToggle &&
          !(hideParentCommentToggleForTopLevel &&
            comment.parentCommentId === comment.topLevelCommentId
          ) &&
          /* We're often comparing null to undefined, so we need to explicitly use a double-eq-negation */
          /* eslint-disable-next-line eqeqeq */
          parentCommentId != comment.parentCommentId &&
          /* eslint-disable-next-line eqeqeq */
          parentAnswerId != comment.parentCommentId &&
        <ShowParentComment
          comment={comment}
          active={showParentState}
          onClick={toggleShowParent}
        />
      }
      {(showCollapseButtons || collapsed) &&
        <a className={classes.collapse} onClick={toggleCollapse}>
          <>[<span className={classes.collapseCharacter}>{collapsed ? "+" : "-"}</span>]</>
        </a>
      }
      {singleLineCollapse && <a className={classes.collapse} onClick={() =>
        setSingleLine && setSingleLine(true)
      }>
        [<span>{collapsed ? "+" : "-"}</span>]
      </a>}
      <CommentUserName
        comment={comment}
        className={classes.username}
      />
      {!comment.llmRunId && <UserCommentMarkers
        user={comment.user}
        isPostAuthor={authorIsPostAuthor && !commentIsTopLevelShortform}
        className={classes.userMarkers}
      />}
      <CommentsItemDate {...commentLinkProps} />
      {showModeratorCommentAnnotation &&
        <span className={classes.moderatorHat}>
          {moderatorCommentAnnotation}
        </span>
      }
      {!comment.debateResponse && !comment.rejected && !comment.draft && <SmallSideVote
        document={comment}
        collectionName="Comments"
        hideKarma={post?.hideCommentKarma}
      />}

      {post && <CommentOutdatedWarning comment={comment} post={post}/>}

      {comment.nominatedForReview &&
        <Link
          to={`/nominations/${comment.nominatedForReview}`}
          className={classes.metaNotice}
        >
          {`Nomination for ${comment.nominatedForReview} Review`}
        </Link>
      }

      {comment.reviewingForReview &&
        <Link
          to={getReviewLink(comment.reviewingForReview)}
          className={classes.metaNotice}
        >
          {`Review for ${reviewingForReview} Review`}
        </Link>
      }

      {(rightSectionElements || menuVisible) && <span className={classNames(classes.rightSection, {
        [classes.rightSectionInFlow]: !!rightSectionElements,
      })}>
        {rightSectionElements}
        {menuVisible &&
          <AnalyticsContext pageElementContext="tripleDotMenu">
            <CommentsMenu
              className={classes.menu}
              comment={comment}
              post={post}
              tag={tag}
              showEdit={setShowEdit}
            />
          </AnalyticsContext>
        }
      </span>}
    </div>
  );
}

export default CommentsItemMeta;


